// Dependencies
import xlsx, { IContent, IColumn } from "json-as-xlsx";

import { get } from "lodash";
import { AnnualEmissions } from "@/api/services/Dashboard";

// Utils
import format from "@/services/Utils/format";
import { resolveVisibleColumns } from "../utils/projectBreakdownColumns";

// Types
import {
  DashboardEmissionBudgetType,
  DashboardEmissionByTypeType,
  DashboardEmissionByEpdTypeType,
  DashboardEmissionByModelType,
  DashboardEmissionByStageType,
  DashboardMappingType,
  DashbboardEmissionByMaterialType,
  DashboardProjectBreakdownType,
  DashboardProjectDataType,
  DashboardProjectBreakdownClassificationType,
  DrilldownEmissionByTypeType,
  DrilldownEmissionByMaterialType,
  ProjectBreakdownColumn,
} from "../types/DashboardType";

type Column = IColumn;

function column(label: string, path: string): Column {
  return {
    label,
    value: (row: IContent) => get(row, path, ""),
  };
}

const EXCEL_SHEET_NAME_MAX_LENGTH = 31;

function truncateSheetName(name: string): string {
  return name.length > EXCEL_SHEET_NAME_MAX_LENGTH ? name.slice(0, EXCEL_SHEET_NAME_MAX_LENGTH) : name;
}

function emissionByMaterialSheetTitle(drilldownEmissionByMaterial: DrilldownEmissionByMaterialType) {
  if (drilldownEmissionByMaterial.id) {
    return truncateSheetName(`Emission by "${drilldownEmissionByMaterial.name}"`);
  }

  if (drilldownEmissionByMaterial.tag) {
    return truncateSheetName(`Emission by "${drilldownEmissionByMaterial.tagName}"`);
  }

  return "Emission by Material";
}

function emissionByTypeSheetTitle(drilldownEmissionByType: DrilldownEmissionByTypeType) {
  return drilldownEmissionByType.id ? truncateSheetName(`Emission by "${drilldownEmissionByType.name}"`) : "Emission by Type";
}

// The sheet's own name columns stand in for the table's Name column.
function getVisibleColumns(projectId: string | null, emissionUnit: string): ProjectBreakdownColumn[] {
  return resolveVisibleColumns(projectId, emissionUnit).filter((col) => col.key !== "name");
}

// Map column key to data path in the flattened row
function getDataPath(columnKey: string): string {
  // Handle nested properties
  if (columnKey.startsWith("emissionByStage.") || columnKey.startsWith("epd.")) {
    return columnKey;
  }

  // Map simple fields
  const pathMap: Record<string, string> = {
    model: "model",
    elements: "elements",
    quantity: "quantity",
    unit: "preferredUnit", // Note: Excel uses 'unit' but data has 'preferredUnit'
    lifetime: "lifetime",
    lifetimeOffset: "lifetimeOffset",
    emission: "emission",
    isReused: "isReused",
    weight: "weight",
    declarationNumber: "declarationNumber",
    numberOfReplacements: "numberOfReplacements",
  };

  return pathMap[columnKey] || columnKey;
}

const dashboardAsExcel = ({
  emissionBudget,
  emissionByEpdType,
  emissionByMaterial,
  emissionByModel,
  emissionByStage,
  emissionByType,
  emissionUnit,
  mapping,
  projectBreakdown,
  projectData,
  emissionOverTime,
  drilldownEmissionByMaterial,
  drilldownEmissionByType,
  projectId,
}: {
  emissionBudget: DashboardEmissionBudgetType;
  emissionByEpdType: DashboardEmissionByEpdTypeType;
  emissionByMaterial: DashbboardEmissionByMaterialType;
  emissionByModel: DashboardEmissionByModelType;
  emissionByStage: DashboardEmissionByStageType;
  emissionByType: DashboardEmissionByTypeType;
  emissionUnit: string;
  mapping: DashboardMappingType;
  projectBreakdown: DashboardProjectBreakdownType;
  projectData: DashboardProjectDataType;
  emissionOverTime: AnnualEmissions[];
  drilldownEmissionByMaterial: DrilldownEmissionByMaterialType;
  drilldownEmissionByType: DrilldownEmissionByTypeType;
  projectId: string | null;
}) => {
  const overviewSheet = {
    sheet: "Overview",
    columns: [
      column(`Used (${emissionUnit})`, "used.value"),
      column(`Threshold (${emissionUnit})`, "threshold"),
      column("Calculation type", "calculationType.label"),
      column("Consideration period (years)", "considerationPeriod"),
      column("", ""),
      column("Materials (%)", "material.percentage"),
      column("Transport (%)", "transport.percentage"),
    ],
    content: [
      {
        ...emissionBudget,
        ...mapping,
        calculationType: {
          id: emissionBudget.calculationType.id,
          label: emissionBudget.calculationType.label,
          variant: emissionBudget.calculationType.variant,
        },
      } as IContent,
    ],
  };

  const projectDataSheet = {
    sheet: "Project Data",
    columns: [
      column("Name", "name"),
      column("Stage", "stage"),
      column("Building type", "buildingType"),
      column("Project Number", "projectNumber"),
      column("Year of commissioning", "yearOfCommissioning"),
      column("Area above ground", "areaAboveGround"),
      column("Area below ground", "areaBelowGround"),
      column("Total area", "totalArea"),
      column("Floors above ground", "floorsAboveGround"),
      column("Floors below ground", "floorsBelowGround"),
      column("Heated area above ground", "heatedAreaAboveGround"),
      column("Heated area below ground", "heatedAreaBelowGround"),
      column("Street", "address.street"),
      column("Street number", "address.streetNumber"),
      column("Postal code", "address.postalCode"),
      column("City", "address.city"),
      column("Country", "address.country"),
    ],
    content: [
      {
        ...projectData,
        calculationType: {
          id: emissionBudget.calculationType.id,
          label: emissionBudget.calculationType.label,
          variant: emissionBudget.calculationType.variant,
        },
      } as IContent,
    ],
  };

  const emissionByEpdTypeSheet = {
    sheet: "Emission by Env. Data Type",
    columns: [
      column("EPD Type", "name"),
      column(`Emission (${emissionUnit})`, "emission"),
      column("Percentage (%)", "percentage"),
      column("Materials", "materials"),
    ],
    content: Object.entries(emissionByEpdType).map(([key, value]) => ({
      name: key,
      emission: value.emission,
      percentage: value.percentage,
      materials: value.materials,
    })),
  };

  const emissionByModelSheet = {
    sheet: "Emission by Model",
    columns: [
      column("Model", "name"),
      column(`Emission (${emissionUnit})`, "emission"),
      column("Percentage", "percentage"),
      column("Materials", "materials"),
    ],
    content: Object.entries(emissionByModel).map(([key, value]) => ({
      name: key,
      emission: value.emission,
      percentage: value.percentage,
      materials: value.materials,
    })),
  };

  const emissionByTypeSheet = {
    sheet: emissionByTypeSheetTitle(drilldownEmissionByType),
    columns: [column("Series", "name"), column("Construction type", "label"), column(`Emission (${emissionUnit})`, "value")],
    content: Object.entries(emissionByType)
      .map(([key, value]) => (value.entries || []).map((entry) => ({ name: key, ...entry })))
      .flat(),
  };

  const emissionByMaterialSheet = {
    sheet: emissionByMaterialSheetTitle(drilldownEmissionByMaterial),
    columns: [column("Series", "name"), column("Construction type", "label"), column(`Emission (${emissionUnit})`, "value")],
    content: Object.entries(emissionByMaterial)
      .map(([key, value]) => (value.entries || []).map((entry) => ({ name: key, ...entry })))
      .flat(),
  };

  const emissionByStageSheet = {
    sheet: "Emission by Stage",
    columns: [column("Series", "name"), column("Construction type", "label"), column(`Emission (${emissionUnit})`, "value")],
    content: Object.entries(emissionByStage)
      .map(([key, value]) => (value.entries || []).map((entry) => ({ name: key, ...entry })))
      .flat(),
  };

  const emissionOverTimeSheet = {
    sheet: "Cumulative Emissions",
    columns: [
      column("Year", "year"),
      column(`Embodied (${emissionUnit})`, "embodiedEmissions"),
      column(`Embodied with Module D (${emissionUnit})`, "embodiedEmissionsWithModuleD"),
      column(`Operational (${emissionUnit})`, "operationalEmissions"),
    ],
    content: emissionOverTime
      .sort((a, b) => a.year - b.year)
      .map((item) => ({
        year: item.year,
        embodiedEmissions: item.embodiedEmissions ?? 0,
        embodiedEmissionsWithModuleD: item.embodiedEmissionsWithModuleD ?? 0,
        operationalEmissions: item.operationalEmissions ?? 0,
      })),
  };

  const emissionByEmbodiedSheet = (() => {
    if (emissionOverTime.length === 0) {
      return {
        sheet: "Emission by Embodied",
        columns: [
          column("Type", "label"),
          column(`Emission (${emissionUnit})`, "emission"),
          column("Percentage (%)", "percentage"),
        ],
        content: [],
      };
    }

    const lastItem = emissionOverTime[emissionOverTime.length - 1];
    const { embodiedEmissions, operationalEmissions } = lastItem;
    const total = embodiedEmissions + operationalEmissions;
    const embodiedPercentage = (embodiedEmissions / total) * 100;
    const operationalPercentage = (operationalEmissions / total) * 100;

    const content = [];
    if (embodiedPercentage > 0) {
      content.push({
        label: "Embodied",
        emission: embodiedEmissions,
        percentage: embodiedPercentage,
      });
    }
    if (operationalPercentage > 0) {
      content.push({
        label: "Operational",
        emission: operationalEmissions,
        percentage: operationalPercentage,
      });
    }

    return {
      sheet: "Emission by Embodied",
      columns: [
        column("Type", "label"),
        column(`Emission (${emissionUnit})`, "emission"),
        column("Percentage (%)", "percentage"),
      ],
      content,
    };
  })();

  function _flattenClassification(c: DashboardProjectBreakdownClassificationType[string]): IContent[] {
    const rows: unknown[] = [];

    rows.push({
      identifier: "classification",
      classification: c,
      classificationCode: c.classification.code,
      classificationName: c.classification.name,
    });
    const { name: classificationName, code: classificationCode } = c.classification;

    for (const childClassification of Object.values(c.classifications ?? {})) {
      rows.push(..._flattenClassification(childClassification));
    }

    for (const type of Object.values(c.types ?? {})) {
      const { name: typeName } = type;
      rows.push({
        ...type,
        identifier: "type",
        classificationName,
        classificationCode,
        typeName,
      });

      for (const construction of Object.values(type.constructions ?? {})) {
        const { name: constructionName } = construction;
        rows.push({
          ...construction,
          identifier: "construction",
          classificationName,
          classificationCode,
          typeName,
          constructionName,
        });

        for (const material of Object.values(construction.materials ?? {})) {
          const { name: materialName } = material;
          rows.push({
            ...material,
            identifier: "material",
            classificationName,
            classificationCode,
            typeName,
            constructionName,
            materialName,
          });
        }
      }

      for (const material of Object.values(type.materials ?? {})) {
        const { name: materialName } = material;
        rows.push({
          ...material,
          identifier: "material",
          classificationName,
          classificationCode,
          typeName,
          materialName,
        });
      }
    }

    return rows as IContent[];
  }

  // Create project breakdown sheet with error handling
  let projectBreakdownSheet;
  try {
    const visibleColumns = getVisibleColumns(projectId, emissionUnit);

    // Build columns array dynamically
    const projectBreakdownColumns = [
      column("Classification Type", "classificationType"),
      column("Identifier", "identifier"),
      column("Classification", "classificationName"),
      column("Code", "classificationCode"),
      column("Type Name", "typeName"),
      column("Construction Name", "constructionName"),
      column("Material Name", "materialName"),
      // Add visible columns from ProjectBreakdown
      ...visibleColumns.map((col) => {
        const dataPath = getDataPath(col.key);
        const label = col.subLabel ? `${col.label} ${col.subLabel}` : col.label;
        return column(label, dataPath);
      }),
    ];

    // Ensure projectBreakdown and classifications exist
    const classifications = projectBreakdown?.classifications || {};
    const classificationValues = Object.values(classifications);

    const flattenedRows =
      classificationValues.length > 0
        ? classificationValues
            .map((classification) =>
              _flattenClassification(classification as DashboardProjectBreakdownClassificationType[string])
            )
            .flat()
        : [];

    const content = flattenedRows.map((row) => {
      const baseRow: IContent = {
        classificationType: projectBreakdown?.type || "",
        identifier: row.identifier || "",
        classificationName: row.classificationName || "",
        classificationCode: row.classificationCode || "",
        typeName: row.typeName || "",
        constructionName: row.constructionName || "",
        materialName: row.materialName || "",
      };

      // Add visible column data
      for (const col of visibleColumns) {
        const dataPath = getDataPath(col.key);
        const value = get(row, dataPath, null);
        baseRow[dataPath] = value;
      }

      return baseRow;
    });

    projectBreakdownSheet = {
      sheet: "Project Breakdown",
      columns: projectBreakdownColumns,
      content: content,
    };
  } catch (_) {
    // Create a minimal sheet with just headers if there's an error
    projectBreakdownSheet = {
      sheet: "Project Breakdown",
      columns: [
        column("Classification Type", "classificationType"),
        column("Identifier", "identifier"),
        column("Classification", "classificationName"),
        column("Code", "classificationCode"),
        column("Type Name", "typeName"),
        column("Construction Name", "constructionName"),
        column("Material Name", "materialName"),
      ],
      content: [],
    };
  }

  // Ensure projectBreakdownSheet is defined
  if (!projectBreakdownSheet) {
    projectBreakdownSheet = {
      sheet: "Project Breakdown",
      columns: [
        column("Classification Type", "classificationType"),
        column("Identifier", "identifier"),
        column("Classification", "classificationName"),
        column("Code", "classificationCode"),
        column("Type Name", "typeName"),
        column("Construction Name", "constructionName"),
        column("Material Name", "materialName"),
      ],
      content: [],
    };
  }

  const allSheets = [
    overviewSheet,
    projectDataSheet,
    emissionByEpdTypeSheet,
    emissionByEmbodiedSheet,
    emissionByModelSheet,
    emissionByTypeSheet,
    emissionByMaterialSheet,
    emissionByStageSheet,
    emissionOverTimeSheet,
    projectBreakdownSheet,
  ];

  // Validate all sheets before passing to xlsx
  const validSheets = allSheets.filter((s) => {
    const isValid = s && s.sheet && Array.isArray(s.columns) && Array.isArray(s.content);
    return isValid;
  });

  xlsx(validSheets, {
    fileName: `${projectData.name}-insights-${format.date(new Date())}`,
  });
};

export default dashboardAsExcel;
